import React, { useState, useEffect } from 'react';
import { FiSave, FiPlus, FiTrash2, FiToggleLeft, FiToggleRight, FiBell } from 'react-icons/fi';
import api from '../../api';

const ContentSection = ({
  websiteData,
  handleInputChange,
  handleSave,
  tenantId
}) => {
  const [saving, setSaving] = useState(false);
  const [notify, setNotify] = useState({ visible: false, type: '', message: '' });
  const [togglingId, setTogglingId] = useState(null);
  
  // Initialize with default structure to prevent undefined errors
  const [localContentData, setLocalContentData] = useState({
    announcements: {
      primary: {
        title: '',
        content: '',
        isActive: true,
        startDate: new Date().toISOString().split('T')[0],
        endDate: ''
      },
      secondary: []
    }
  });

  // Helper function to format date for input
  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  // Sync local state with prop changes
  useEffect(() => {
    if (websiteData?.content) {
      const content = websiteData.content;
      setLocalContentData({
        announcements: {
          primary: {
            title: content.announcements?.primary?.title || '',
            content: content.announcements?.primary?.content || '',
            isActive: content.announcements?.primary?.isActive !== undefined ? content.announcements?.primary?.isActive : true,
            startDate: content.announcements?.primary?.startDate || new Date().toISOString().split('T')[0],
            endDate: content.announcements?.primary?.endDate || ''
          },
          secondary: (content.announcements?.secondary || []).map((announcement) => ({
            ...announcement,
            isNew: false
          }))
        }
      });
    } else {
      setLocalContentData({
        announcements: {
          primary: {
            title: '',
            content: '',
            isActive: true,
            startDate: new Date().toISOString().split('T')[0],
            endDate: ''
          },
          secondary: []
        }
      });
    }
  }, [websiteData?.content]);

  // Notification timeout
  useEffect(() => {
    if (!notify.visible) return;
    const timer = setTimeout(() => setNotify({ visible: false, type: '', message: '' }), 3000);
    return () => clearTimeout(timer);
  }, [notify.visible]);

  // Show notification helper
  const showNotification = (message, type = 'success') => {
    setNotify({ visible: true, type, message });
  };

  // Handle content save
  const handleSaveContent = async () => {
    setSaving(true);
    try {
      // Save primary announcement
      if (localContentData.announcements.primary) {
        const primaryResponse = await api.put(
          `/api/website/${tenantId}/announcements/primary`,
          localContentData.announcements.primary
        );
        if (!primaryResponse?.data?.success) {
          throw new Error(primaryResponse?.data?.message || 'Failed to save primary announcement');
        }
      }

      // Save secondary announcements - each individually
      let savedSecondaryAnnouncements = [...(localContentData.announcements?.secondary || [])];
      if (localContentData.announcements.secondary && localContentData.announcements.secondary.length > 0) {
        savedSecondaryAnnouncements = [];
        for (const announcement of localContentData.announcements.secondary) {
          if (announcement.isNew) {
            // Create new secondary announcement
            const { id, isNew, ...announcementPayload } = announcement;
            const secondaryResponse = await api.post(
              `/api/website/${tenantId}/announcements/secondary`,
              announcementPayload
            );

            if (!secondaryResponse?.data?.success) {
              throw new Error(secondaryResponse?.data?.message || 'Failed to add secondary announcement');
            }

            savedSecondaryAnnouncements.push({
              ...announcement,
              ...(secondaryResponse?.data?.data || {}),
              isNew: false
            });
          } else if (announcement.id) {
            // Update existing announcement
            const secondaryResponse = await api.put(
              `/api/website/${tenantId}/announcements/secondary/${announcement.id}`,
              announcement
            );

            if (!secondaryResponse?.data?.success) {
              throw new Error(secondaryResponse?.data?.message || 'Failed to save secondary announcement');
            }

            savedSecondaryAnnouncements.push({
              ...announcement,
              ...(secondaryResponse?.data?.data || {}),
              isNew: false
            });
          }
        }
      }

      const updatedContentData = {
        ...localContentData,
        announcements: {
          ...localContentData.announcements,
          secondary: savedSecondaryAnnouncements
        }
      };

      setLocalContentData(updatedContentData);
      showNotification('Announcements saved successfully', 'success');
      // Update parent component
      handleInputChange('content', null, updatedContentData);
    } catch (error) {
      console.error('Error saving announcements:', error);
      showNotification(error?.message || 'Failed to save announcements', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Update primary announcement
  const updatePrimaryAnnouncement = (field, value) => {
    setLocalContentData(prev => ({
      ...prev,
      announcements: {
        ...prev.announcements,
        primary: {
          ...prev.announcements.primary,
          [field]: value
        }
      }
    }));
  };

  // Add secondary announcement
  const addSecondaryAnnouncement = async () => {
    const newAnnouncement = {
      id: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      title: 'New Announcement',
      content: 'Enter announcement content here...',
      type: 'info',
      isActive: true,
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      isNew: true
    };

    setLocalContentData(prev => ({
      ...prev,
      announcements: {
        ...prev.announcements,
        secondary: [...(prev.announcements?.secondary || []), newAnnouncement]
      }
    }));

    showNotification('Announcement added', 'success');
  };

  // Remove secondary announcement
  const removeSecondaryAnnouncement = async (announcementToRemove) => {
    const id = announcementToRemove?.id;

    if (!id || announcementToRemove?.isNew) {
      // Just remove from local state if this is a new unsaved announcement
      setLocalContentData(prev => ({
        ...prev,
        announcements: {
          ...prev.announcements,
          secondary: (prev.announcements?.secondary || []).filter(a => a.id !== id)
        }
      }));
      showNotification('Announcement removed', 'success');
      return;
    }

    setLocalContentData(prev => ({
      ...prev,
      announcements: {
        ...prev.announcements,
        secondary: (prev.announcements?.secondary || []).filter(a => a.id !== id)
      }
    }));

    try {
      await api.delete(`/api/website/${tenantId}/announcements/secondary/${id}`);
      showNotification('Announcement removed', 'success');
    } catch (error) {
      console.error('Error removing announcement:', error);
      showNotification('Failed to remove announcement', 'error');
      // Revert the removal
      const response = await api.get(`/api/website/${tenantId}/announcements`);
      if (response?.data?.success) {
        setLocalContentData(prev => ({
          ...prev,
          announcements: {
            ...prev.announcements,
            secondary: response.data.data.allSecondary || []
          }
        }));
      }
    }
  };

  // Toggle secondary announcement active status
  const toggleSecondaryAnnouncement = async (index, announcement) => {
    if (!announcement.id || announcement.isNew) return;

    setTogglingId(announcement.id);
    try {
      const response = await api.patch(
        `/api/website/${tenantId}/announcements/secondary/${announcement.id}/toggle`
      );
      
      if (response?.data?.success) {
        // Update local state
        setLocalContentData(prev => ({
          ...prev,
          announcements: {
            ...prev.announcements,
            secondary: (prev.announcements?.secondary || []).map((a, i) =>
              i === index ? { ...a, isActive: !a.isActive } : a
            )
          }
        }));
        showNotification(response.data.message, 'success');
      } else {
        throw new Error(response?.data?.message || 'Failed to toggle announcement');
      }
    } catch (error) {
      console.error('Error toggling announcement:', error);
      showNotification('Failed to toggle announcement', 'error');
    } finally {
      setTogglingId(null);
    }
  };

  // Update secondary announcement
  const updateSecondaryAnnouncement = (index, field, value) => {
    setLocalContentData(prev => ({
      ...prev,
      announcements: {
        ...prev.announcements,
        secondary: (prev.announcements?.secondary || []).map((announcement, i) => 
          i === index ? { ...announcement, [field]: value } : announcement
        )
      }
    }));
  };

  return (
    <div className="mt-2 grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
      {/* Editor Main Canvas */}
      <div className="lg:col-span-2 space-y-6">
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-xs">
          {/* Section Toolbar Header */}
          <div className="px-5 py-4 border-b border-slate-150 bg-white flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-base font-bold text-slate-900">Announcements Management</h3>
              <p className="text-xs text-slate-500 mt-0.5">Manage broadcasts and notice banner objects distributed across view portals.</p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {notify.visible && (
                <span className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border ${
                  notify.type === 'success' 
                    ? 'bg-emerald-50 text-emerald-800 border-emerald-200' 
                    : notify.type === 'info'
                    ? 'bg-blue-50 text-blue-800 border-blue-200'
                    : 'bg-rose-50 text-rose-800 border-rose-200'
                }`}>
                  {notify.message}
                </span>
              )}

              <button
                onClick={handleSaveContent}
                disabled={saving}
                className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-lg shadow-xs text-white transition-all ${
                  saving 
                    ? 'bg-slate-300 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-offset-1 focus:ring-blue-500'
                }`}
              >
                <FiSave className="h-3.5 w-3.5" />
                {saving ? 'Saving...' : 'Save Announcements'}
              </button>
            </div>
          </div>

          <div className="p-5 space-y-6">
            {/* Group Card - Primary Announcement */}
            <div className="bg-slate-50/60 rounded-xl border border-slate-200 p-4 space-y-4">
              <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiBell className="text-amber-500 h-4 w-4" />
                Primary Alert Banner
              </h4>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Headline Title</label>
                  <input
                    type="text"
                    value={localContentData.announcements?.primary?.title || ''}
                    onChange={(e) => updatePrimaryAnnouncement('title', e.target.value)}
                    className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="Important Notice Heading Label"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Message Body Content</label>
                  <textarea
                    rows={3}
                    value={localContentData.announcements?.primary?.content || ''}
                    onChange={(e) => updatePrimaryAnnouncement('content', e.target.value)}
                    className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                    placeholder="Enter the full message details summary context here..."
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Release Start Date</label>
                    <input
                      type="date"
                      value={formatDateForInput(localContentData.announcements?.primary?.startDate)}
                      onChange={(e) => updatePrimaryAnnouncement('startDate', e.target.value)}
                      className="w-full text-xs px-3 py-1.5 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Expiration End Date</label>
                    <input
                      type="date"
                      value={formatDateForInput(localContentData.announcements?.primary?.endDate)}
                      onChange={(e) => updatePrimaryAnnouncement('endDate', e.target.value)}
                      className="w-full text-xs px-3 py-1.5 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200 mt-1">
                  <span className="text-xs font-semibold text-slate-700">Display Blueprint Element Toggle</span>
                  <label className="inline-flex items-center cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={localContentData.announcements?.primary?.isActive || false}
                      onChange={(e) => updatePrimaryAnnouncement('isActive', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="relative w-9 h-5 bg-slate-200 peer-focus:outline-hidden rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Group Card - Secondary Announcements */}
            <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
              <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Secondary News Feeds ({localContentData.announcements?.secondary?.length || 0} of 5 slots)</h4>
                <button
                  type="button"
                  onClick={addSecondaryAnnouncement}
                  disabled={(localContentData.announcements?.secondary || []).length >= 5}
                  className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg text-white transition-all shadow-xs ${
                    (localContentData.announcements?.secondary || []).length >= 5 
                      ? 'bg-slate-300 cursor-not-allowed' 
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  <FiPlus className="h-3.5 w-3.5" />
                  <span>Add Feed</span>
                </button>
              </div>

              <div className="space-y-4">
                {(localContentData.announcements?.secondary || []).map((announcement, index) => (
                  <div key={announcement.id || index} className="border border-slate-200 rounded-xl p-4 bg-slate-50/30 flex flex-col gap-3 group relative hover:border-slate-300 transition-all">
                    
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-2">
                        <label className="text-xs font-semibold text-slate-600">Feed Headline</label>
                        
                        {announcement.id && !announcement.isNew && (
                          <button
                            type="button"
                            onClick={() => toggleSecondaryAnnouncement(index, announcement)}
                            disabled={togglingId === announcement.id}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold border transition-all ${
                              announcement.isActive
                                ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                                : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'
                            } ${togglingId === announcement.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {announcement.isActive ? <FiToggleRight className="h-3.5 w-3.5" /> : <FiToggleLeft className="h-3.5 w-3.5" />}
                            <span>{announcement.isActive ? 'Active Mode' : 'Inactive'}</span>
                          </button>
                        )}
                      </div>
                      
                      <button
                        type="button"
                        onClick={() => removeSecondaryAnnouncement(announcement)}
                        className="p-1.5 text-rose-500 hover:text-rose-700 hover:bg-rose-50 border border-transparent hover:border-rose-100 rounded-lg transition-all self-start shrink-0"
                        title="Remove news feed row"
                      >
                        <FiTrash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    <input
                      type="text"
                      value={announcement.title || ''}
                      onChange={(e) => updateSecondaryAnnouncement(index, 'title', e.target.value)}
                      className="w-full text-xs px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-1 focus:ring-blue-500 transition-all text-slate-800 font-semibold"
                      placeholder="Announcement Title"
                    />

                    <div>
                      <label className="block text-[11px] font-semibold text-slate-500 mb-1">Body Context Description</label>
                      <textarea
                        rows={2}
                        value={announcement.content || ''}
                        onChange={(e) => updateSecondaryAnnouncement(index, 'content', e.target.value)}
                        className="w-full text-xs px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-1 focus:ring-blue-500 transition-all resize-none text-slate-600"
                        placeholder="Announcement text summary..."
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 pt-1 items-end">
                      <div>
                        <label className="block text-[11px] font-semibold text-slate-500 mb-1">Notice Type Tag</label>
                        <select
                          value={announcement.type || 'info'}
                          onChange={(e) => updateSecondaryAnnouncement(index, 'type', e.target.value)}
                          className="w-full text-xs px-2.5 py-1.5 border border-slate-200 bg-white rounded-md focus:outline-hidden text-slate-700 font-medium"
                        >
                          <option value="info">Information</option>
                          <option value="warning">Warning Log</option>
                          <option value="alert">Alert Code</option>
                          <option value="success">Success Node</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-[11px] font-semibold text-slate-500 mb-1">Start Release</label>
                        <input
                          type="date"
                          value={formatDateForInput(announcement.startDate)}
                          onChange={(e) => updateSecondaryAnnouncement(index, 'startDate', e.target.value)}
                          className="w-full text-xs px-2.5 py-1 border border-slate-200 rounded-md bg-white text-slate-700"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] font-semibold text-slate-500 mb-1">End Expiration</label>
                        <input
                          type="date"
                          value={formatDateForInput(announcement.endDate)}
                          onChange={(e) => updateSecondaryAnnouncement(index, 'endDate', e.target.value)}
                          className="w-full text-xs px-2.5 py-1 border border-slate-200 rounded-md bg-white text-slate-700"
                        />
                      </div>

                      <div className="flex items-center justify-start sm:justify-center h-9">
                        <label className="flex items-center gap-2 select-none cursor-pointer">
                          <input
                            type="checkbox"
                            checked={announcement.isActive || false}
                            onChange={(e) => updateSecondaryAnnouncement(index, 'isActive', e.target.checked)}
                            className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                          />
                          <span className="text-[11px] font-bold text-slate-600">Active</span>
                        </label>
                      </div>
                    </div>
                  </div>
                ))}

                {(localContentData.announcements?.secondary || []).length === 0 && (
                  <div className="text-center py-6 border border-dashed border-slate-200 bg-slate-50/50 rounded-xl">
                    <p className="text-xs text-slate-500">No secondary notifications logged inside this tracking map layer.</p>
                    <button
                      type="button"
                      onClick={addSecondaryAnnouncement}
                      className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg text-blue-700 bg-blue-50 hover:bg-blue-100 transition-all"
                    >
                      <FiPlus className="h-3.5 w-3.5" />
                      <span>Build first row node</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Live System Preview Column */}
      <div className="lg:col-span-1">
        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-xs space-y-4 lg:sticky lg:top-24">
          <div>
            <h3 className="text-sm font-bold text-slate-900">Broadcast View Mockup</h3>
            <p className="text-xs text-slate-400 mt-0.5">Real-time portal news module dashboard display monitor.</p>
          </div>

          <div className="space-y-4 min-h-[300px] bg-slate-50 p-4 border border-slate-150 rounded-xl">
            {/* Render Primary Notice Element layout box */}
            {localContentData.announcements?.primary?.title && localContentData.announcements?.primary?.isActive && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex gap-2 animate-fade-in">
                <span className="text-amber-500 text-xs shrink-0 mt-0.5">⚠️</span>
                <div className="min-w-0 flex-1">
                  <h5 className="font-bold text-amber-900 text-xs truncate">{localContentData.announcements.primary.title}</h5>
                  {localContentData.announcements.primary.content && (
                    <p className="text-amber-800 text-[11px] mt-0.5 leading-relaxed break-words">{localContentData.announcements.primary.content}</p>
                  )}
                </div>
              </div>
            )}
            
            {/* Render Secondary Items lists preview boxes */}
            {(localContentData.announcements?.secondary || []).filter(a => a.isActive).length > 0 && (
              <div className="space-y-2.5">
                <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Secondary Broadcast List</h4>
                
                {(localContentData.announcements.secondary || []).filter(a => a.isActive).slice(0, 3).map((announcement, index) => {
                  const typeStyles = {
                    warning: 'bg-orange-50 border-orange-200 text-orange-900 desc:text-orange-800',
                    alert: 'bg-rose-50 border-rose-200 text-rose-900 desc:text-rose-800',
                    success: 'bg-emerald-50 border-emerald-200 text-emerald-900 desc:text-emerald-800',
                    info: 'bg-blue-50 border-blue-200 text-blue-900 desc:text-blue-800'
                  }[announcement.type || 'info'];

                  return (
                    <div key={index} className={`p-3 rounded-xl border flex flex-col gap-0.5 shadow-2xs ${typeStyles}`}>
                      <p className="font-bold text-xs truncate">{announcement.title}</p>
                      {announcement.content && (
                        <p className="text-[11px] leading-relaxed opacity-90 break-words">{announcement.content}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {!localContentData.announcements?.primary?.title && 
             (localContentData.announcements?.secondary || []).filter(a => a.isActive).length === 0 && (
              <div className="text-center py-12 flex flex-col items-center justify-center">
                <FiBell className="text-slate-300 h-5 w-5 mb-1.5 animate-pulse" />
                <p className="text-slate-400 text-xs italic">No active broadcasting nodes deployed.</p>
              </div>
            )}
          </div>

          <p className="text-[10px] text-slate-400 leading-relaxed text-center italic bg-slate-50 py-2 border border-slate-150 rounded-lg">
            Changes applied inside this feed panel configuration must be updated manually by clicking Save above.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ContentSection;